import {
  ProductDetailsFragment,
  ReviewBaseFragment,
} from '@sdk/gqlTypes/generatedOperations';

import urljoin from 'url-join';

import { generatePath, slugify, decodeKey } from '@utils/core';
import { translate } from '@utils/translations';
import { paths } from '@paths';

const getReviewsStructuredData = (reviews): any[] =>
  reviews?.edges?.map(({ node }) => ({
    '@type': 'Review',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: node.rating,
    },
    author: {
      '@type': 'Person',
      name: node.fullName,
    },
  }));

const getAggregateReviewsStructuredData = (reviews): any => ({
  '@type': 'AggregateRating',
  ratingValue:
    reviews && reviews.length
      ? reviews.reduce((acc, r) => r.rating + acc, 0) / reviews.length
      : 5,
  reviewCount: reviews.length || 1,
});

/*
const getVariantsStructuredData = (variants): any[] => {
  const inStock = 'https://schema.org/InStock';
  const outOfStock = 'https://schema.org/OutOfStock';
  return variants.map((variant) => ({
    '@type': 'Offer',
    availability: variant.isAvailable ? inStock : outOfStock,
    itemCondition: 'https://schema.org/NewCondition',
    price: variant.pricing.price.gross.amount.toFixed(2),
    priceCurrency: variant.pricing.price.gross.currency,
    sku: variant.sku,
  }));
};
*/

const getOffersStructuredData = (product: ProductDetailsFragment): any => ({
  '@type': 'AggregateOffer',
  availability: 'https://schema.org/InStock',
  itemCondition: 'https://schema.org/NewCondition',
  lowPrice: product.pricing.priceRange.start.gross.amount.toFixed(2),
  highPrice: product.pricing.priceRange.stop.gross.amount.toFixed(2),
  offerCount: product.variants.length,
  priceCurrency: product.pricing.priceRange.start.gross.currency,
  shippingDetails: {
    '@type': 'OfferShippingDetails',
    shippingRate: {
      '@type': 'MonetaryAmount',
      value: '0',
      currency: product.pricing.priceRange.start.gross.currency,
    },
    deliveryTime: {
      '@type': 'ShippingDeliveryTime',
      handlingTime: {
        '@type': 'QuantitativeValue',
        minValue: product.productType.shippingMin,
        maxValue: product.productType.shippingMax,
      },
    },
  },
});

export const structuredData = (product: ProductDetailsFragment): string => {
  const images = product.images.map((image) => new URL(image.url));
  const { reviews } = product;
  return JSON.stringify({
    '@context': 'https://schema.org/',
    '@type': 'Product',
    brand: 'Reno',
    description: !product.seoDescription
      ? `${product.name}`
      : `${product.seoDescription}`,
    image: images,
    manufacturer: 'Reno sp zoo',
    name: !product.seoTitle ? `${product.name}` : `${product.seoTitle}`,
    // offers: getVariantsStructuredData(variants),
    offers: getOffersStructuredData(product),
    productID: product.slug,
    sku: product.slug,
    url: urljoin(
      process.env.NEXT_PUBLIC_VERCEL_URL || 'https://www.renodoors.eu/',
      generatePath(paths.product, {
        slug: slugify(translate(product, 'name')),
        id: decodeKey(product.id, 'Product'),
      })
    ),
    aggregateRating: getAggregateReviewsStructuredData(reviews),
    review: getReviewsStructuredData(reviews),
  });
};

export const structuredDataReview = (reviews: ReviewBaseFragment[]): string =>
  JSON.stringify({
    '@context': 'https://schema.org/',
    '@type': 'Reviews',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue:
        reviews && reviews.length
          ? reviews.reduce((acc, r) => r.rating + acc, 0) / reviews.length
          : 5,
      reviewCount: reviews.length || 1,
    },
    review:
      reviews && reviews.length
        ? reviews.map((e, _, arr) => {
            const images = e.product?.images.map((image) => new URL(image.url));
            return {
              '@type': 'Review',
              itemReviewed: {
                '@type': 'Product',
                brand: 'Reno',
                image: images,
                manufacturer: 'Reno sp zoo',
                name: e.product?.name ? e.product?.name : 'sliding doors',
                productID: e.product?.slug ? e.product?.slug : 'sliding-doors',
                sku: e.product?.slug ? e.product?.slug : 'sliding-doors',
              },
              author: {
                '@type': 'Person',
                name: e.fullName,
              },
              datePublished: e.createdAt,
              name: e.title,
              reviewBody: e.description,
              reviewRating: {
                '@type': 'Rating',
                bestRating: arr.reduce((max, val) => {
                  let sum = max;
                  if (val.rating > sum) {
                    sum = val.rating;
                  }
                  return sum;
                }, 5),
                ratingValue: e.rating,
                worstRating: arr.reduce((min, val) => {
                  let sum = min;
                  if (val.rating < sum) {
                    sum = val.rating;
                  }
                  return sum;
                }, 5),
              },
            };
          })
        : null,
  });
